import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./OrderSummary.scss";
import useOrders from "../firebase-files/hooks/useOrders";
import { useDocument } from "../firebase-files/hooks/useDocument";
import useCart from "../firebase-files/hooks/useCart";
import { projectFirestore } from "../firebase-config/config";
import { useCollection } from "../firebase-files/hooks/useCollection";

const OrderSummary = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { documents: orders } = useCollection("orders");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { clearCart } = useCart();

  const { document: currencyDocument } = useDocument("settings", "currency");

  /**
   * 🔒 SAFER: Use Firestore transaction to prevent overselling
   */
  const updateProductStock = async (orderData) => {
    try {
      await projectFirestore.runTransaction(async (transaction) => {
        for (const product of orderData.products || []) {
          const productRef = projectFirestore
            .collection("products")
            .doc(product.id);
          const productDoc = await transaction.get(productRef);

          if (!productDoc.exists) {
            throw new Error(`Product ${product.name || product.id} not found`);
          }

          const currentStock = productDoc.data().stock ?? 0;
          const newStock = currentStock - (product.quantity ?? 0);

          if (newStock < 0) {
            throw new Error(
              `Insufficient stock for ${product.name || "Unknown product"}`
            );
          }

          transaction.update(productRef, { stock: newStock });
        }
      });
    } catch (err) {
      console.error("Error updating stock:", err);
      setError("Failed to update product stock. Please contact support.");
    }
  };

  useEffect(() => {
    if (orders) {
      setLoading(false);
    }
  }, [orders]);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orders) return;

      setLoading(true);
      try {
        const order = orders.find((o) => o.id === id);

        if (order) {
          // 🔎 Basic schema safety
          if (!Array.isArray(order.products)) {
            throw new Error("Order data is corrupted");
          }

          setOrderData(order);
          await updateProductStock(order);
          clearCart();
          setError(null);
        } else {
          setError("Order not found");
        }
      } catch (err) {
        console.error("Order fetch error:", err);
        setError("Something went wrong while loading your order.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, orders]);

  if (loading) {
    return <div className="order-summary-page loading">Loading...</div>;
  }

  if (error) {
    return <div className="order-summary-page error">Error: {error}</div>;
  }

  if (!orderData) {
    return (
      <div className="order-summary-page error">
        No order found with ID: {id}
      </div>
    );
  }

  return (
    <div className="order-summary-page">
      <div className="thank-you-message">
        <span className="material-symbols-outlined">check_circle</span>
        <h2>Thank You for Ordering!</h2>
        <p>
          <strong>Order ID : </strong> {id}
        </p>
      </div>

      <div className="order-details">
        <div className="order-date">
          <h4>Order Date:</h4>
          <p>
            {orderData.createdAt?.toDate
              ? orderData.createdAt.toDate().toLocaleString("en-IN")
              : "N/A"}
          </p>
        </div>

        <div className="order-total">
          <h4>Total Order Value :</h4>
          <p>
            {currencyDocument?.symbol || "₹"}
            {orderData.totalPrice ?? 0}
          </p>
        </div>

        <div className="order-payment-method">
          <h4>Payment Method:</h4>
          <p>
            {orderData.paymentMethod === "cash" ? "Cash" : "Online Payment"}
          </p>
        </div>

        {orderData.shippingAddress && (
          <div className="order-address">
            <h4>Address</h4>
            <div className="order-address-type">
              <span className="material-symbols-outlined">
                {orderData.shippingAddress.type === "home"
                  ? "home"
                  : orderData.shippingAddress.type === "work"
                  ? "work"
                  : "location_on"}
              </span>
              <span>
                {(orderData.shippingAddress.type || "Unknown")
                  .charAt(0)
                  .toUpperCase() +
                  (orderData.shippingAddress.type || "").slice(1)}
              </span>
            </div>
            <h5>{orderData.shippingAddress.name || "N/A"}</h5>
            <p>
              {orderData.shippingAddress.houseNumber || ""}{" "}
              {orderData.shippingAddress.streetLineOne || ""}{" "}
              {orderData.shippingAddress.streetLineTwo || ""}{" "}
              {orderData.shippingAddress.landmark || ""}{" "}
              {orderData.shippingAddress.city || ""}{" "}
              {orderData.shippingAddress.state || ""}{" "}
              {orderData.shippingAddress.pincode || ""}
            </p>
          </div>
        )}
      </div>

      <div className="order-items">
        <h2>Items</h2>
        <div className="order-items-container">
          {orderData.products.map((product, index) => {
            const safeImage = Array.isArray(product.images)
              ? typeof product.images[0] === "object"
                ? product.images[0]?.url
                : product.images[0]
              : typeof product.images === "object"
              ? product.images?.url
              : product.images;

            return (
              <div className="order-item" key={index}>
                <img
                  src={safeImage || "/fallback-image.png"}
                  alt={product.name || "product"}
                />

                <div className="order-item-details">
                  <h3>{product.name || "Unnamed Product"}</h3>
                  <p>
                    Price: {currencyDocument?.symbol || "₹"}{" "}
                    {product.price ?? 0}
                  </p>
                  <p>Quantity: {product.quantity ?? 0}</p>
                  <p>
                    Total: {currencyDocument?.symbol || "₹"}{" "}
                    {(product.price ?? 0) * (product.quantity ?? 0)}
                  </p>
                  <p>
                    {product.color ? `Color: ${product.color}` : ""}{" "}
                    {product.size ? `Size: ${product.size}` : ""}{" "}
                    {product.variantName
                      ? `Variant: ${product.variantName}`
                      : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button
        onClick={() => {
          navigate("/orders");
        }}
        className="view-orders-button"
      >
        View All Orders
      </button>
    </div>
  );
};

export default OrderSummary;
